import React from 'react';
import { motion } from 'framer-motion';
import { PaletteIcon, FileTextIcon, ZapIcon, GlobeIcon, ShieldCheckIcon, LockIcon, CreditCardIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { TrustLine } from './TrustLine';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { offer } from '../data/offer';
const chips = [{
  icon: FileTextIcon,
  label: '100% PDF'
}, {
  icon: ZapIcon,
  label: 'Acceso inmediato'
}, {
  icon: GlobeIcon,
  label: 'Toda LATAM'
}, {
  icon: PaletteIcon,
  label: '5 a 9 años'
}];
const stats = [{
  value: '6',
  label: 'Tomos'
}, {
  value: '5',
  label: 'Bonos'
}, {
  value: '315',
  label: 'Páginas'
}, {
  value: '60',
  label: 'Imprimibles'
}, {
  value: 'LATAM',
  label: 'Toda la región'
}];
export function Hero({
  guaranteeEnabled
}: {
  guaranteeEnabled: boolean;
}) {
  const trust = [...(guaranteeEnabled ? [{
    icon: ShieldCheckIcon,
    label: 'Garantía 7 días*'
  }] : []), {
    icon: LockIcon,
    label: 'Pago seguro'
  }, {
    icon: CreditCardIcon,
    label: 'Moneda local'
  }];
  return <header className="bg-ink" data-id="element-122" data-mp="3j6fw9kdl53b7">
      <div className="mx-auto max-w-6xl px-5 pb-14 pt-8 sm:pt-12" data-id="element-123" data-mp="3o85b7b3xi14k">
        <div className="grid items-center gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12" data-id="element-124" data-mp="3o59d9ms2zcg4">
          <div className="text-center lg:text-left" data-id="element-125" data-mp="39a32ynlycrgz">
            <p className="flex items-center justify-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-brand sm:text-sm lg:justify-start" data-id="element-126" data-mp="3e8mur4n88xh9">
              <PaletteIcon className="h-4 w-4" aria-hidden="true" data-id="element-127" data-mp="30fybc1yzvc0e" />
              ReadyKit Digital™
            </p>

            <h1 className="mt-4 text-3xl font-black leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl" data-id="element-128" data-mp="2qjv9kx821fdz">
              Entretenimiento sin pantallas.
              <span className="block text-brand" data-id="element-129" data-mp="2vj7tpa7jk8cv">Listo desde hoy.</span>
            </h1>

            <p className="mx-auto mt-4 max-w-xl text-base font-semibold text-cream/90 sm:text-lg lg:mx-0" data-id="element-130" data-mp="2hjoyfhztwrhz">
              120 actividades, juegos, experimentos e imprimibles para niños de 5 a 9 años. Sin buscar ni improvisar.
            </p>

            <motion.img src={offer.mockupImage} alt="MegaPack Antipantallas Listo: caja con los 6 tomos, imprimibles y sello de 6 tomos más 5 bonos" className="mx-auto mt-6 w-full max-w-[240px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)] sm:max-w-[290px] lg:hidden" initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-131" data-mp="nbbeh30z0j7e" />

            <p className="mt-6 text-base font-bold text-cream" data-id="element-132" data-mp="15zzqt0le1g7k">
              Precio de lanzamiento: <span className="font-black text-brand" data-id="element-133" data-mp="114stcaipp7r3">{offer.price}</span>
            </p>
            <div className="mt-3" data-id="element-134" data-mp="x0utfxlkbswz">
              <CTAButton href={offer.checkoutUrl} data-id="element-135" data-mp="3lsp3hdsd4jef">Quiero mi MegaPack</CTAButton>
            </div>
            <LocalCurrencyNote className="mt-3 lg:justify-start" data-id="element-136" data-mp="3hrlmgnykwhbm" />
            <div className="mt-4 lg:[&_ul]:justify-start" data-id="element-137" data-mp="3htg8y6pi0hht">
              <TrustLine items={trust} data-id="element-138" data-mp="1xacgb5dntnua" />
            </div>

            <ul className="mt-6 flex flex-wrap justify-center gap-2 lg:justify-start" data-id="element-139" data-mp="21awco6qesnfd">
              {chips.map(({
              icon: Icon,
              label
            }) => <li key={label} className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-sm font-bold text-cream" data-id="element-140" data-mp="268y4ph5tbx2z">
                  <Icon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-141" data-mp="2fzsugvi9pet6" />
                  {label}
                </li>)}
            </ul>
          </div>

          <motion.img src={offer.mockupImage} alt="MegaPack Antipantallas Listo: caja con los 6 tomos, imprimibles y sello de 6 tomos más 5 bonos" className="hidden w-full max-w-[420px] justify-self-center drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)] lg:block" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-142" data-mp="2kx2ge16g78c9" />
        </div>

        <dl className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-5" data-id="element-143" data-mp="49t2nrbsmsra">
          {stats.map((s, i) => <div key={s.label} className={`bg-surface px-4 py-4 text-center ${i === stats.length - 1 ? 'col-span-2 sm:col-span-1' : ''}`} data-id="element-144" data-mp="e8imhpn92na6" data-mpi={i}>
              <dt className="sr-only" data-id="element-145" data-mp="3hpr180mol70o">{s.label}</dt>
              <dd data-id="element-146" data-mp="3hqjzsebhkq7q">
                <span className="block text-2xl font-black text-white" data-id="element-147" data-mp="3hnxrudkoo8oz">{s.value}</span>
                <span className="mt-1 block text-xs font-black uppercase tracking-widest text-brand" data-id="element-148" data-mp="3cp32stw4hnwf">{s.label}</span>
              </dd>
            </div>)}
        </dl>

        <p className="mx-auto mt-5 max-w-2xl text-center text-xs leading-relaxed text-muted/70" data-id="element-149" data-mp="3mnslupzr9ee9">
          {guaranteeEnabled && 'Garantía sujeta a las condiciones de venta. '}
          Moneda local según países habilitados en el checkout.
        </p>
      </div>
    </header>;
}